import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Calendar, Clock, Film, ExternalLink, Mail, Send, CheckCircle, Loader2, Bell, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Films à la une
const featuredFilms = [
  {
    title: 'Microstar',
    director: 'Leopold Kraus',
    releaseDate: '8 juillet 2026',
    duration: '1h 30min',
    genre: 'Comedie dramatique',
    cast: ['Abraham Wapler', 'Felix Lefebvre', 'Raika Hazanavicius', 'Maddie Kulicka', 'Michael Zindel', 'Fred Tousch', 'Hugo Becker'],
    posterImage: 'https://customer-assets.emergentagent.com/job_cinema-mission/artifacts/0ai2x95g_WhatsApp%20Image%202026-07-08%20at%2010.26.09.jpeg',
    allocineUrl: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=1000030390.html',
    synopsis: "Lucas a tout pour reussir : il est beau, jeune, en couple avec la femme de ses reves. Mais voila, il vit dans l'ombre de son meilleur ami, Elie, une star des reseaux sociaux. Quand Elie lui propose de participer a un podcast ensemble, Lucas y voit l'opportunite de briller enfin. Mais le succes a un prix...",
    awards: [],
  },
  {
    title: 'Un Grand Raccourci',
    director: 'Armand Foex & Clement Devilliers',
    releaseDate: '12 aout 2026',
    duration: '1h 32min',
    genre: 'Comedie dramatique',
    cast: ['Sara Music', 'Louise Music', 'Jules Poirier', 'Francois Le Brix'],
    posterImage: 'https://customer-assets-rejwkqb3.emergentagent.net/job_cinema-mission/artifacts/o6w4r8ta_image.png',
    allocineUrl: 'https://www.allocine.fr/film/fichefilm_gen_cfilm=1000041528.html',
    synopsis: "Dans la campagne francaise, quatre jeunes adultes se retrouvent lors d'un ete qui va bouleverser leurs vies. Entre amours naissantes, amities mises a l'epreuve et secrets de famille, ils vont devoir faire face a leurs choix et prendre un grand raccourci vers l'age adulte.",
    awards: [],
  },
];

const AgendaPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const filmRef = useRef(null);
  const isFilmInView = useInView(filmRef, { once: true, margin: '-50px' });
  
  const newsletterRef = useRef(null);
  const isNewsletterInView = useInView(newsletterRef, { once: true, margin: '-50px' });

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setIsSubscribed(true);
      setEmail('');
    } catch (err) {
      // Simuler le succès pour la démo
      setIsSubscribed(true);
      setEmail('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="agenda-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#050505] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[150px]" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-[150px]" />
          </div>
          
          <div className="container-custom relative z-10">
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <span className="overline mb-6 block">Agenda & Actualités</span>
              <h1 className="text-4xl md:text-6xl font-medium mb-6 leading-tight">
                Nos prochaines sorties et <span className="text-gradient">programme des séances</span>
              </h1>
              <p className="text-[#a1a1aa] text-lg md:text-xl leading-relaxed max-w-3xl">
                Découvrez les films que nous soutenons, nos prochaines sorties en salle 
                et le programme des séances que nous organiserons.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Films à l'affiche */}
        <section className="py-16 md:py-24 bg-[#0a0a0c]" ref={filmRef} data-testid="featured-film">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isFilmInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <span className="overline mb-4 block">À la une</span>
              <h2 className="text-3xl md:text-4xl font-medium">
                À l'affiche
              </h2>
            </motion.div>

            <div className="space-y-16">
              {featuredFilms.map((featuredFilm, index) => (
                <motion.div
                  key={featuredFilm.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={isFilmInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.2 + index * 0.2 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12"
                >
                  {/* Affiche */}
                  <div className="lg:col-span-4">
                    <div className="relative group">
                      <div className="absolute -inset-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                        <img
                          src={featuredFilm.posterImage}
                          alt={`Affiche ${featuredFilm.title}`}
                          className="w-full h-auto"
                        />
                        {/* Badge date de sortie */}
                        <div className="absolute top-4 left-4 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-full font-semibold text-sm flex items-center gap-2">
                          <Calendar size={16} />
                          {featuredFilm.releaseDate}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Infos */}
                  <div className="lg:col-span-8">
                    <div className="glass rounded-3xl p-8 md:p-10 h-full">
                      {/* Header */}
                      <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span className="px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-semibold rounded-full">
                          {featuredFilm.genre}
                        </span>
                        <span className="px-3 py-1 bg-white/10 text-white/80 text-xs font-medium rounded-full">
                          {featuredFilm.duration}
                        </span>
                        <span className="px-3 py-1 bg-white/10 text-white/80 text-xs font-medium rounded-full flex items-center gap-1">
                          <Clock size={12} />
                          En salle le {featuredFilm.releaseDate}
                        </span>
                      </div>

                      <h3 className="text-3xl md:text-4xl font-medium text-white mb-2">
                        {featuredFilm.title}
                      </h3>
                      <p className="text-[#D4AF37] text-lg mb-6">
                        Un film de {featuredFilm.director}
                      </p>

                      {/* Cast */}
                      <div className="mb-6">
                        <p className="text-[#71717a] text-sm mb-2">Avec</p>
                        <p className="text-white">
                          {featuredFilm.cast.join(', ')}
                        </p>
                      </div>

                      {/* Synopsis */}
                      <div className="mb-6">
                        <p className="text-[#71717a] text-sm mb-2">Synopsis</p>
                        <p className="text-[#a1a1aa] leading-relaxed">
                          {featuredFilm.synopsis}
                        </p>
                      </div>

                      {/* Awards */}
                      {featuredFilm.awards.length > 0 && (
                        <div className="mb-8">
                          <p className="text-[#71717a] text-sm mb-3">Recompenses</p>
                          <div className="flex flex-wrap gap-2">
                            {featuredFilm.awards.map((award) => (
                              <span 
                                key={award}
                                className="px-3 py-2 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-lg flex items-center gap-2"
                              >
                                <Star size={12} />
                                {award}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CTA */}
                      <a
                        href={featuredFilm.allocineUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="allocine-link"
                      >
                        <Film size={18} />
                        Info
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-16 md:py-24 bg-[#050505]" ref={newsletterRef} data-testid="newsletter-section">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isNewsletterInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="glass rounded-3xl p-10 md:p-16">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 flex items-center justify-center"
                >
                  <Bell size={40} className="text-[#D4AF37]" />
                </motion.div>

                <h2 className="text-2xl md:text-3xl font-medium text-white mb-4">
                  Restez informés de notre actualité et nos prochaines séances
                </h2>
                <p className="text-[#a1a1aa] text-lg mb-8 max-w-lg mx-auto">
                  Inscrivez-vous à notre newsletter pour recevoir nos actualités 
                  et être informés de nos prochaines sorties.
                </p>

                {isSubscribed ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center justify-center gap-3 text-[#D4AF37]"
                  >
                    <CheckCircle size={24} />
                    <span className="font-medium">Merci pour votre inscription !</span>
                  </motion.div>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                    <div className="flex-grow relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        required
                        className="w-full pl-12 pr-4 py-4 rounded-full bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        data-testid="newsletter-email"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
                      data-testid="newsletter-submit"
                    >
                      {isSubmitting ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <>
                          <Send size={18} />
                          S'abonner
                        </>
                      )}
                    </button>
                  </form>
                )}

                <p className="text-[#71717a] text-xs mt-6">
                  Pas de spam. Désabonnement possible à tout moment.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AgendaPage;
